// Global Types
import type {
  $GeoLocation,
} from 'common.types';

// Global Components
import * as React from 'react';
import {
  Marker,
  MarkerClusterer,
} from '@react-google-maps/api';

// Helpers
import _ from 'lodash';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import config from '../../../config';

// Components
import Map from '../Map';
import MapRegion from '../MapRegion';

// Types
type $Props = {
  activeRegionFlag: string;
  locations: Array<$GeoLocation>;
  onLocationSelect: (arg0: $GeoLocation) => void;
  onRegionSelect: (RegionFlag: string) => void;
  reCenter: $GeoLocation | Date | null;
  regions: Array<{
    center: $GeoLocation;
    flag: string;
    path: Array<Array<$GeoLocation>>;
    zoom: number;
  }>;
};

type $State = {
  center: $GeoLocation;
  zoom: number;
};

class MapClusterView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      center: {
        lat: 0,
        lng: 0,
      },
      zoom: 0,
    };
  }

  componentDidMount(): void {
    this.moveToActiveRegionCenter();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      activeRegionFlag,
      reCenter,
    } = this.props;

    if (isDiff(
      activeRegionFlag,
      prevProps.activeRegionFlag,
    ) || isDiff(
      reCenter,
      prevProps.reCenter,
    )) {
      this.reCenter();
    }
  }

  reCenter() {
    const {
      reCenter,
    } = this.props;

    if (reCenter !== null) {
      const lat: number | undefined = _.get(
        reCenter,
        'lat',
      );

      const lng: number | undefined = _.get(
        reCenter,
        'lng',
      );

      if (typeof lat !== 'undefined' && typeof lng !== 'undefined') {
        this.setState({
          center: {
            lat,
            lng,
          },
          zoom: 12,
        });

        return;
      }
    }

    this.moveToActiveRegionCenter();
  }

  moveToActiveRegionCenter(): void {
    const {
      activeRegionFlag,
      regions,
    } = this.props;

    const activeRegion = _.find(
      regions,
      {
        flag: activeRegionFlag,
      },
    );

    if (activeRegion) {
      const {
        center,
        zoom,
      } = activeRegion;

      this.setState({
        center,
        zoom,
      });
    }
  }

  renderCountries() {
    const {
      activeRegionFlag,
      onRegionSelect,
      regions,
    } = this.props;

    return regions.map((region) => {
      const active: boolean = activeRegionFlag === region.flag;

      return (
        <MapRegion
          active={active}
          key={region.flag}
          onClick={() => onRegionSelect(region.flag)}
          region={region}
        />
      );
    });
  }

  renderLocation(location: $GeoLocation, clusterer) {
    const {
      onLocationSelect,
    } = this.props;
    const {
      center,
    } = this.state;

    const type = isDiff(
      center,
      location,
    ) === false ? 'active' : 'inactive';

    return (
      <Marker
        clusterer={clusterer}
        icon={{
          scaledSize: {
            equals: () => true,
            height: 42,
            width: 42,
          },
          url: `${config.CDN_URL}/static/map/marker-${type}.png`,
        }}
        key={`${location.lat}${location.lng}`}
        onClick={() => {
          onLocationSelect(location);

          this.setState({
            center: location,
            zoom: 12,
          });
        }}
        position={location}
      />
    );
  }

  renderLocations(clusterer) {
    const {
      locations,
    } = this.props;

    return (
      <>
        {locations.map((location: $GeoLocation) => this.renderLocation(
          location,
          clusterer,
        ))}
      </>
    );
  }

  renderCluster() {
    const style = {
      fontFamily: 'Nunito, ui-sans-serif',
      fontWeight: '100',
      height: 42,
      textColor: 'rgb(20, 83, 136)',
      textSize: 20,
      url: `${config.CDN_URL}/static/map/marker1.png`,
      width: 42,
    };
    const styles = [style];

    return (
      <MarkerClusterer styles={styles}>
        {(clusterer) => this.renderLocations(clusterer)}
      </MarkerClusterer>
    );
  }

  render() {
    const {
      center,
      zoom,
    } = this.state;

    return (
      <Map
        center={center}
        draggable
        height={300}
        zoom={zoom}
      >
        <>
          {this.renderCluster()}
          {this.renderCountries()}
        </>
      </Map>
    );
  }
}

export default MapClusterView;
